import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Form, Formik } from "formik";

import { getFullName } from "@vega-api/custom/utils/user";
import { getGetLimitedPartnerUsersPermissionViewQueryOptions } from "@vega-api/generated/coinvest-deal-permission/coinvest-deal-permission";
import {
  useGetDefaultTemplateName,
  useGetTemplateContentsByTemplateName,
} from "@vega-api/generated/outreach-template-contents/outreach-template-contents";
import { type CoinvestPerson } from "@vega-api/generated/types/coinvestPerson";
import { type DealShortlistItemResponse } from "@vega-api/generated/types/dealShortlistItemResponse";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import {
  addSelectItemFieldsToUser,
  getUserOrFreeformEmailItems,
  type SearchMultiSelectItemWithUser,
  SearchUserMultiselect,
} from "@vega-core/components/Form/Controls/Input/SearchUser/SearchUserMultiselect";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { TextareaField } from "@vega-core/components/Form/Fields/TextareaField";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { m } from "@vega-core/paraglide/messages";

import { useSendEmailMutation } from "../ProspectsTable/hooks/useSendEmailMutation";
import { useSenderSearch } from "../ProspectsTable/hooks/useSenderSearch";

const toCoinvestPerson = (
  user: SearchMultiSelectItemWithUser,
): CoinvestPerson => ({
  id: user.id,
  fullName: { firstName: user.fullName, lastName: "" },
  email: user.email,
});

type NotifyUsersFormValues = {
  emailSubject: string;
  emailText: string;
  to: SearchMultiSelectItemWithUser[];
  cc: SearchMultiSelectItemWithUser[];
  sender: SearchMultiSelectItemWithUser | null;
};

type NotifyUsersStepProps = {
  dealId: string;
  dealName: string;
  prospects: DealShortlistItemResponse[];
  onBack: () => void;
  onClose: () => void;
  onSuccess?: () => void;
};

export const NotifyUsersStep = ({
  dealId,
  dealName,
  prospects,
  onBack,
  onClose,
  onSuccess,
}: NotifyUsersStepProps) => {
  const publicConfig = usePublicConfig();
  const emailTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const [ccSearchValue, setCcSearchValue] = useState("");

  const isMultiple = prospects.length > 1;
  const firstProspect = prospects[0];

  const { data: lpUsersData, isLoading: isUsersLoading } = useQuery(
    getGetLimitedPartnerUsersPermissionViewQueryOptions(
      dealId,
      firstProspect.limitedPartner.id,
      {
        query: {
          select: (data) => data.data,
          enabled: !isMultiple,
        },
      },
    ),
  );

  const { data: defaultTemplateNameData } = useGetDefaultTemplateName({
    query: { enabled: isMultiple },
  });

  const { data: templateContentsData, isPending: isTemplateContentsPending } =
    useGetTemplateContentsByTemplateName(
      defaultTemplateNameData?.data?.templateName ?? "",
      {
        query: {
          enabled: isMultiple && !!defaultTemplateNameData?.data?.templateName,
        },
      },
    );

  const lpUsersSearchItems = (lpUsersData?.users ?? []).map(
    addSelectItemFieldsToUser,
  );

  const relationshipSelectedItem =
    !isMultiple && firstProspect.relationship
      ? addSelectItemFieldsToUser({
          id: firstProspect.relationship.id,
          fullName: getFullName(firstProspect.relationship.fullName),
          email: firstProspect.relationship.email,
        })
      : null;

  const {
    gpUsersSearchItems,
    onSenderSearchChange,
    isSenderSearchLoading,
    senderSearchValue,
  } = useSenderSearch({
    dealId,
    selectedItemToInclude: relationshipSelectedItem,
  });

  const shortlistItemToLimitedPartnerMap = prospects.reduce(
    (acc, prospect) => {
      acc[prospect.id] = prospect.limitedPartner.id;

      return acc;
    },
    {} as Record<string, string>,
  );

  const {
    sendSingleEmail,
    sendBulkEmail,
    isPending: isSendEmailPending,
  } = useSendEmailMutation({
    dealId,
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
    shortlistItemToLimitedPartnerMap,
  });

  const initialValues: NotifyUsersFormValues = isMultiple
    ? {
        emailSubject: templateContentsData?.data?.templateHeaders.subject ?? "",
        emailText: templateContentsData?.data?.textPart ?? "",
        to: [],
        cc: [],
        sender: null,
      }
    : {
        emailSubject: m.coInvestments_editEmail_defaultSubject({
          dealName,
          gpName: publicConfig.global.companyName,
          lpName: firstProspect.limitedPartner.name,
        }),
        emailText: m.coInvestments_editEmail_defaultTemplate({
          lpName: firstProspect.limitedPartner.name,
          gpName: publicConfig.global.companyName,
          dealLink: firstProspect.dealLink,
        }),
        to: lpUsersSearchItems,
        cc: [],
        sender: relationshipSelectedItem,
      };

  const validateForm = (values: NotifyUsersFormValues) => {
    const errors: Partial<Record<keyof NotifyUsersFormValues, string>> = {};

    if (!values.emailSubject.trim()) {
      errors.emailSubject =
        m.coInvestments_editEmail_validation_subjectRequired();
    }

    if (!values.emailText.trim()) {
      errors.emailText = m.coInvestments_editEmail_validation_required();
    }

    if (!isMultiple && values.to.length === 0) {
      errors.to = m.coInvestments_editEmail_validation_toRequired();
    }

    if (!isMultiple && !values.sender) {
      errors.sender = m.coInvestments_editEmail_validation_fromRequired();
    }

    return errors;
  };

  const handleSubmit = (values: NotifyUsersFormValues) => {
    if (isMultiple) {
      sendBulkEmail(prospects.map((p) => p.id));

      return;
    }

    if (!values.sender) return;

    sendSingleEmail({
      dealShortlistItemId: firstProspect.id,
      emailBody: values.emailText,
      emailSubject: values.emailSubject,
      mainRecipients: values.to.map(toCoinvestPerson),
      ccRecipients: values.cc.map(toCoinvestPerson),
      opportunityOwner: toCoinvestPerson(values.sender),
    });
  };

  const handleAddDealLink = (
    currentValue: string,
    setFieldValue: (field: string, value: string) => void,
  ) => {
    if (!emailTextAreaRef.current) return;

    const cursor = emailTextAreaRef.current.selectionEnd;
    const newValue =
      currentValue.slice(0, cursor) +
      firstProspect.dealLink +
      currentValue.slice(cursor);
    setFieldValue("emailText", newValue);
  };

  if (!isMultiple && isUsersLoading) {
    return <LoadingSpinner />;
  }

  if (isMultiple && isTemplateContentsPending) {
    return <LoadingSpinner />;
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validateForm}
    >
      {({ submitForm, values, errors, setFieldValue }) => {
        const ccItems = getUserOrFreeformEmailItems({
          users: lpUsersSearchItems,
          selectedItems: values.cc,
          searchValue: ccSearchValue,
        });

        return (
          <ActionaryModal
            ModalButtonsGroupProps={{
              disabled: isSendEmailPending,
              mainAction: submitForm,
              mainActionText: m.coInvestments_share_sendEmail(),
              secondaryAction: onBack,
              secondaryActionText: m.btn_back(),
              thirdActionElement: isMultiple ? undefined : (
                <ThemedButton
                  dataTestId="share-modal-add-deal-link-button"
                  onClick={() =>
                    handleAddDealLink(values.emailText, setFieldValue)
                  }
                  type="button"
                  variant="secondary"
                >
                  {m.coInvestments_editEmail_addDealLink_button()}
                </ThemedButton>
              ),
            }}
            dataTestId="share-modal-notify-users"
            description={m.coInvestments_share_notifyDescription()}
            fluidHeight
            open
            setOpen={onClose}
            title={m.coInvestments_share_notifyTitle()}
          >
            <Form>
              <div className="flex flex-col gap-4 items-stretch text-start mx-auto sm:min-w-[36rem]">
                {isMultiple ? null : (
                  <>
                    <SearchUserMultiselect
                      error={errors.to as string}
                      items={lpUsersSearchItems}
                      leftLabel={m.coInvestments_editEmail_emailSentTo_label()}
                      multiple
                      name="to"
                      onSelectionChange={(newUsers) =>
                        setFieldValue("to", newUsers)
                      }
                      selectedValues={values.to}
                    />
                    <SearchUserMultiselect
                      autoHighlight
                      items={ccItems}
                      leftLabel={m.coInvestments_editEmail_cc_label()}
                      multiple
                      name="cc"
                      onSearchChange={setCcSearchValue}
                      onSelectionChange={(newUsers) => {
                        setFieldValue("cc", newUsers);
                        setCcSearchValue("");
                      }}
                      selectedValues={values.cc}
                    />
                    <SearchUserMultiselect
                      error={errors.sender}
                      hint={m.coInvestments_editEmail_bcc_notice()}
                      isLoading={isSenderSearchLoading}
                      items={gpUsersSearchItems}
                      leftLabel={m.coInvestments_editEmail_emailSentFrom_label()}
                      multiple={false}
                      name="sender"
                      onSearchChange={onSenderSearchChange}
                      onSelectionChange={(newSender) => {
                        setFieldValue("sender", newSender);
                        onSenderSearchChange("");
                      }}
                      searchInputValue={senderSearchValue.trim() || undefined}
                      selectedValues={values.sender}
                    />
                  </>
                )}
                <InputField
                  leftLabel={m.coInvestments_editEmail_subjectLabel()}
                  name="emailSubject"
                  placeholder={m.coInvestments_editEmail_subjectPlaceholder()}
                  withBorder
                />
                <TextareaField
                  dataTestId="share-modal-email-text"
                  name="emailText"
                  placeholder={m.coInvestments_editEmail_placeholder()}
                  ref={emailTextAreaRef}
                  resize={false}
                  rows={12}
                  withBorder
                />
              </div>
            </Form>
          </ActionaryModal>
        );
      }}
    </Formik>
  );
};
